import { Loader } from "@googlemaps/js-api-loader";
import axios from "axios";
import { cloneDeep } from "lodash";
import React, { Component, SyntheticEvent } from "react";
import { AppContext } from "../../common/AppContext";
import axiosClient from "../../common/AxiosClient";
import { Dialog } from "../../components/dialogs/Dialog";
import { AddressSelector } from "../../components/selectors/AddressSelector";
import { withLocale, WithLocaleProps } from "../../providers/LocaleProvider";
import type {
  AddEditAddressDialogProps as AddEditAddressDialogOwnProps,
  AddEditAddressDialogState,
  AddressData,
  GeolocationResponse,
  PlacePrediction,
  PlaceDetailResult,
  GoogleAutoCompleteService,
  GooglePlacesService,
  GooglePlacesLibrary,
  GoogleMapsLoader
} from "../../types/views/addEditAddress.types";

type AddEditAddressDialogProps = AddEditAddressDialogOwnProps &
  WithLocaleProps & {
    [key: string]: unknown;
  };

class AddEditAddressDialogBase extends Component<
  AddEditAddressDialogProps,
  AddEditAddressDialogState
> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static googleApiKey = import.meta.env.VITE_GOOGLE_API_KEY;
  static googleGeolocationApiPath = `https://www.googleapis.com/geolocation/v1/geolocate?key=${AddEditAddressDialogBase.googleApiKey}`;
  static googleGeocodingApiPath = `https://maps.googleapis.com/maps/api/geocode/json?key=${AddEditAddressDialogBase.googleApiKey}`;

  private loader: GoogleMapsLoader;
  private autoCompleteService: GoogleAutoCompleteService | null = null;
  private placesService: GooglePlacesService | null = null;
  private PlacesServiceStatus: { OK: string; [key: string]: string } | null = null;
  private currentAddress: AddressData | null;

  constructor(props: AddEditAddressDialogProps) {
    super(props);
    this.state = {
      addresses: null,
      searchAddress: "",
      address: props.address || null,
      saving: false,
      countryCode: null,
      successMessage: null
    };

    this.currentAddress = cloneDeep(props.address) || null;

    this.loader = new Loader({
      apiKey: AddEditAddressDialogBase.googleApiKey,
      version: "weekly",
      libraries: ["places"]
    }) as unknown as GoogleMapsLoader;

    this.loader.importLibrary("places").then((google: GooglePlacesLibrary) => {
      this.autoCompleteService = new google.AutocompleteService();
      this.placesService = new google.PlacesService(document.createElement("div"));
      this.PlacesServiceStatus = google.PlacesServiceStatus;
    });
  }

  componentDidMount(): void {
    this.onGetUserLocation();
  }

  onGetUserLocation(): void {
    axios
      .post<GeolocationResponse>(AddEditAddressDialogBase.googleGeolocationApiPath, {})
      .then((res) => {
        if (res.data?.location) {
          this.onGetCountryCode(res.data.location);
        }
      })
      .catch((e: Error) => {})
      .finally(() => {
        // this.context.setLoading('savePractical', false);
        // this.setState({saving: false});
      });
  }

  onGetCountryCode(data: { lat: number; lng: number }): void {
    const path = `${AddEditAddressDialogBase.googleGeocodingApiPath}&latlng=${data?.lat},${data?.lng}&result_type=country`;
    fetch(path, {})
      .then((res) => res.json())
      .then((res) => {
        const code = res?.results[0]?.address_components[0]?.short_name;
        this.setState({ countryCode: code });
      });
  }

  onSearchMapLocation(address: string): void {
    this.setState({ searchAddress: address });

    if (!address || !this.autoCompleteService) return;

    this.context?.setLoading?.("address", true);

    const request = {
      input: address,
      types: ["geocode"],
      componentRestrictions: { country: this.state.countryCode || "au" },
      language: "en-AU"
    };

    this.autoCompleteService
      .getPlacePredictions(request, (results: PlacePrediction[] | null, status: string) => {
        const formattedAddresses: AddressData[] | null =
          results &&
          results.map((addr: PlacePrediction) => {
            return {
              placeId: addr.place_id,
              street: addr.description,
              latitude: 0,
              longitude: 0,
              city: "",
              state: "",
              zipCode: "",
              country: ""
            };
          });

        this.setState({ addresses: formattedAddresses });
      })
      .catch((error: Error) => {})
      .finally(() => {
        this.context?.setLoading?.("address", false);
      });
  }

  onGetAddressDetails(placeId: string | null): void {
    const { isEdit } = this.props;
    if (placeId === null || !this.placesService) {
      return;
    }
    const addressData: AddressData = {
      latitude: 0,
      longitude: 0,
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: ""
    };

    this.context?.setLoading?.("addressDetail", true);

    const request = {
      placeId: placeId,
      fields: ["geometry", "address_components", "formatted_address"],
      componentRestrictions: { country: this.state.countryCode || "au" },
      language: "en-AU"
    };

    this.placesService.getDetails(request, (place: PlaceDetailResult | null, status: string) => {
      if (status !== this.PlacesServiceStatus?.OK || !place) {
        console.error("Google map api error:", status);
      } else {
        const addressComponents = place.address_components;

        addressComponents.forEach((addressComp) => {
          if (addressComp.types.includes("postal_code")) {
            addressData.zipCode = addressComp.long_name;
          }

          if (addressComp.types.includes("country")) {
            addressData.country = addressComp.short_name;
          }

          if (addressComp.types.includes("administrative_area_level_1")) {
            addressData.state = addressComp.long_name;
          }

          if (addressComp.types.includes("administrative_area_level_2")) {
            addressData.city = addressComp.long_name;
          }
          // check case if city is CBD
          if (
            addressComp.types.includes("locality") ||
            (addressComp.types.includes("political") && addressComp.types.length === 1)
          ) {
            addressData.city = addressComp.long_name;
          }
        });

        if (place.formatted_address) {
          addressData.street = place.formatted_address;
        }

        addressData.latitude = place.geometry.location.lat();
        addressData.longitude = place.geometry.location.lng();

        // const changeData = { ...addressData, latitude: latitude, longitude: longitude };

        if (isEdit) {
          this.onEditAddress(addressData);
        } else {
          this.onSaveAddress(addressData);
        }
      }

      this.context?.setLoading?.("addressDetail", false);
    });
  }

  onSaveAddress(addressData: AddressData): void {
    const { history, orgSettings } = this.props;
    if (addressData === null) {
      return;
    }
    this.context?.setLoading?.("savePractical", true);
    this.setState({ saving: true });

    axiosClient
      .post("/Organization/Admin/Address", addressData)
      .then(() => {
        orgSettings?.addAddress(addressData);
        history.push("/settings/company", { successMessage: "Add address successfully" });
      })
      .catch((e) => {})
      .finally(() => {
        this.context?.setLoading?.("savePractical", false);
        this.setState({ saving: false });
      });
  }

  onEditAddress(addressData: AddressData): void {
    const { history, orgSettings } = this.props;

    if (addressData === null) {
      return;
    }

    this.context?.setLoading?.("editPractical", true);
    this.setState({ saving: true });

    axiosClient
      .put("/Organization/Admin/Address", addressData)
      .then(() => {
        orgSettings?.addAddress(addressData);
        history.push("/settings/company", { successMessage: "Edit address successfully" });
      })
      .catch((e) => {})
      .finally(() => {
        this.context?.setLoading?.("editPractical", false);
        this.setState({ saving: false });
      });
  }

  onAddressChange(e: SyntheticEvent, address: AddressData | null): void {
    this.setState({ address: address });
  }

  isValid(address: AddressData | null): boolean {
    if (!this.isAddressValid(address) || !this.hasAddressChange(this.currentAddress, address))
      return false;
    return true;
  }

  isAddressValid(address: AddressData | null): boolean {
    if (!address) {
      return false;
    }

    return (address && address?.street?.length > 0) === true;
  }

  hasAddressChange(currentAddress: AddressData | null, newAddress: AddressData | null): boolean {
    if (
      currentAddress?.street !== newAddress?.street ||
      currentAddress?.city !== newAddress?.city ||
      currentAddress?.state !== newAddress?.state ||
      currentAddress?.zipCode !== newAddress?.zipCode ||
      currentAddress?.country !== newAddress?.country
    ) {
      return true;
    }

    return false;
  }

  onSave() {
    const { address } = this.state;

    if (address) {
      const { placeId } = address;
      this.onGetAddressDetails(placeId || null);
    }
  }

  render() {
    const { isEdit, theme, onClose, translate } = this.props;
    const { addresses, address, saving } = this.state;

    return (
      <Dialog
        open={true}
        theme={theme}
        title={isEdit ? translate("settings.editAddress") : translate("settings.newAddress")}
        onClose={onClose}
        fullWidth={true}
        maxWidth='sm'
        actions={[
          {
            label: isEdit ? translate("common.save") : translate("settings.addAddress"),
            primary: true,
            disabled: !this.isValid(address) || saving,
            onClick: this.onSave.bind(this)
          },
          {
            label: translate("common.cancel"),
            onClick: onClose
          }
        ]}
      >
        <AddressSelector
          label={translate("settings.addressLabel")}
          placeHolder={translate("settings.searchAndAdd")}
          items={addresses ? addresses : null}
          values={address ? address : null}
          onNewItemClick={false}
          getItemLabel={(add: AddressData) => add.street}
          onChange={this.onAddressChange.bind(this)}
          onSearch={this.onSearchMapLocation.bind(this)}
          translate={translate}
        />
      </Dialog>
    );
  }
}

export const AddEditAddressDialog = withLocale(AddEditAddressDialogBase);
